import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { Link } from 'react-router-dom';
import { Box, Text, Button } from 'grommet';
import { FormNext } from 'grommet-icons';
import { Sidebar } from 'react-pro-sidebar';
import { useSpring, animated } from '@react-spring/web';
import Case from 'case';
import {
  FullHeightBox, Container, ErrorNotice,
} from '@Components/Control';
import { paths } from '@Components/configs';
import { useAppTheme } from '@Components/Context';
import { pluralize } from '@Helpers/util';
import companyFeatureFlags from '@Helpers/featureFlags';
import {
  initiateCompanySettingsRequest,
  initiateCyclopsCompanyOrganizationsRequest,
  setCyclopsActiveOrganizationRequest,
  initiateCompanyMaintenanceSettingsRequest,
  fetchCancellationStatus,
} from '@Actions';
import Footer from '../Footer/Footer';
import { MenuDropdownSelect } from '../Header/HeaderComponent';
import {
  AuthBanner,
  MenuSectionHeader,
  MenuItemRenderer,
} from './SidenavComponent';
import {
  SIDENAV_WIDTH,
  SIDENAV_COLLAPSED_WIDTH,
  NAVBAR_DEFAULT_HEIGHT,
  SIDENAV_TOP_PAD,
  SIDENAV_PAGE_CONTENTS_TOP_PAD,
} from '../constants';
import {
  StyledMenu,
  StyledHr,
  StyledStickyFooter,
} from './StyledAuthNavWrapper';

const formatDate = (date) => {
  const dateOptions = { month: 'long', day: '2-digit', year: 'numeric' };
  return new Date(date).toLocaleDateString('en-US', dateOptions);
};

const AuthNavWrapper = ({
  small,
  location,
  bannerProps,
  companyId,
  companyName,
  children,
  companyOrganizations,
  setActiveOrganization,
  fetchCompanyOrganizations,
  fetchCompanySettings,
  fetchCompanyMaintenanceSettings,
  onboardingDataLoading,
  companyFeatures = null,
  companySettings = null,
  companyMaintenanceSettings = null,
  onboardingData = null,
  paymentPending = false,
  paymentLink = null,
  cancellationRequestedAt = null,
  effectiveCancellationDate = null,
  ghlScheduleCancelledAt = null,
  isDemo = false,
  stripeAccountSetupComplete = null,
  stripeAccountId = null,
  loadCancellationStatus,
  activeOrganization = null,
  smallBanner = true,
  hideBanner = false,
  hideFooter = false,
  hideLeftNav = false,
  customReportsConfig = null,
}) => {
  const [navCollapsed, setNavCollapsed] = React.useState(false);
  const [payoutBannerDismissed, setPayoutBannerDismissed] = React.useState(
    () => sessionStorage.getItem('payoutBannerDismissed') === 'true',
  );
  const { transform } = useSpring({
    transform: `rotate(${navCollapsed ? 180 : 0}deg)`,
    config: { mass: 1.5, tension: 250, friction: 35 },
  });
  const { opacity, display } = useSpring({
    opacity: navCollapsed ? 0 : 1,
    display: navCollapsed ? 'none' : 'block',
    delay: navCollapsed ? 0 : 100,
  });
  const {
    theme: { navigation, page, button },
    hideBlacklightSearchCompanies,
    settingsMenuConfig,
    isDarkMode,
  } = useAppTheme();

  const {
    overviewEnabled: overviewEnabledFlag,
    searchEnabled,
    searchConfig,
    reviewEnabled,
    settingsEnabled,
    liveTrackerEnabled,
    reportsEnabled,
    contentIdEnabled,
    contentIdAssetUploadEnabled,
    referralEnabled,
    cyclopsEnabled,
    cyclopsAnalyticsDisabled,
    cyclopsSettingsDisabled,
    isCyclopsDemo,
    fingerprintingEnabled,
  } = companyFeatureFlags(companyFeatures, { stripeAccountSetupComplete, stripeAccountId }) || {};

  const overviewEnabled = isDemo ? true : overviewEnabledFlag;

  const showPayoutSetupBanner = Boolean(
    contentIdEnabled
    && !paymentPending
    && stripeAccountSetupComplete !== true
    && !payoutBannerDismissed,
  );

  const handleDismissPayoutBanner = () => {
    sessionStorage.setItem('payoutBannerDismissed', 'true');
    setPayoutBannerDismissed(true);
  };

  const isContentIdPage = bannerProps?.subTitle === 'Content ID';
  const isReferralsPage = location.pathname === paths.creatorReferrals;
  const contentIdBannerChildren = (
    isContentIdPage && !isReferralsPage && contentIdEnabled && referralEnabled
  ) ? (
    <Link to={paths.creatorReferrals} style={{ textDecoration: 'none' }}>
      <Box
        border={{ color: 'white', size: '1px' }}
        round="var(--app-radius)"
        pad={{ horizontal: '0.9rem', vertical: '0.35rem' }}
        hoverIndicator={{ background: 'rgba(255,255,255,0.15)' }}
      >
        <Text size="0.8rem" weight={600} color="white">Referrals</Text>
      </Box>
    </Link>
    ) : null;

  const cyclopsMenuButtonsDisabled = !isCyclopsDemo
    && (!activeOrganization || !companyOrganizations);
  const cyclopsPageActive = location.pathname.includes('cyclops');
  const organizationsActive = companyOrganizations && activeOrganization;
  const customReports = customReportsConfig && customReportsConfig.find(({ metadata }) => (
    metadata?.config?.companyId === companyId && metadata?.config?.companyName === companyName));
  const withCustomReports = customReports?.metadata?.config?.customReports?.length > 0;
  const withAnalytics = overviewEnabled || reportsEnabled
    || withCustomReports || liveTrackerEnabled;

  const teamsSettingsEnabled = Boolean(searchConfig?.enabledSearchSubtypes?.includes('Sports'));
  const unitTerm = searchConfig?.unitTerm;
  const unitsSettingsEnabled = Boolean(unitTerm);
  const hasFingerprintingConfig = companySettings?.some(({ api }) => api === 'fingerprinting-config');
  const filteredCompanySettings = companySettings?.filter(({ api }) => api !== 'fingerprinting-config');
  const showSettings = Boolean(settingsEnabled && filteredCompanySettings?.length > 0);
  const showAltSettings = Boolean(teamsSettingsEnabled || unitsSettingsEnabled
    || (fingerprintingEnabled && hasFingerprintingConfig));
  const isRedflagUploadCurrent = onboardingData?.youtubeCid?.uploader === 'redflag';
  const stableIsRedflagUploadRef = React.useRef(isRedflagUploadCurrent);
  if (!onboardingDataLoading) {
    stableIsRedflagUploadRef.current = isRedflagUploadCurrent;
  }
  const isRedflagUpload = stableIsRedflagUploadRef.current;

  React.useEffect(() => {
    if (cyclopsEnabled
      && !isCyclopsDemo
      && (!companyOrganizations || companyOrganizations?.length === 0)
    ) {
      fetchCompanyOrganizations();
    }
    if (!companySettings) fetchCompanySettings();
    if (!companyMaintenanceSettings) fetchCompanyMaintenanceSettings();
    loadCancellationStatus();
  }, []);

  const hideSearchFeatures = hideBlacklightSearchCompanies.includes(companyId) || !searchEnabled;
  const hideSideNav = hideLeftNav || (
    !searchEnabled
    && !reviewEnabled
    && !withAnalytics
    && !settingsEnabled
    && !cyclopsEnabled
    && !contentIdEnabled
  );

  const noticeWidth = small ? '100%' : '55%';

  const cancellationNoticeEndDate = effectiveCancellationDate
    ? formatDate(effectiveCancellationDate)
    : null;

  const phase3EndDate = cancellationRequestedAt
    ? formatDate(new Date(cancellationRequestedAt).getTime() + 30 * 24 * 60 * 60 * 1000)
    : null;

  const hasAnyNotice = paymentPending
    || cancellationRequestedAt
    || ghlScheduleCancelledAt
    || showPayoutSetupBanner;

  const onPayoutSetupPage = location.pathname === paths.payoutSetup
    || location.pathname === paths.payoutSetupSuccess;

  const renderErrorNotice = hasAnyNotice && (
    <Box direction="column" gap="0.65rem" width={noticeWidth}>
      {showPayoutSetupBanner && !onPayoutSetupPage && (
        <ErrorNotice
          message="Set up payouts to receive Content ID earnings"
          messageColor="#3A5985"
          iconColor="#3A5985"
          backgroundColor="#EFF8FF"
          handleDismiss={handleDismissPayoutBanner}
        >
          <Box direction="row" align="center" justify="between" gap="0.5rem">
            <Text color={page.secondarytext} size="0.85rem" style={{ lineHeight: 1.25 }}>
              Connect your preferred payout method through Stripe so we know
              where to send your revenue&nbsp;&nbsp;
              <Link to={paths.payoutSetup} style={{ color: page.highlight }}>
                Set up now →
              </Link>
            </Text>
          </Box>
        </ErrorNotice>
      )}
      {paymentPending && (
        <ErrorNotice
          message="Limited feature access"
          messageColor="var(--app-error)"
          iconColor="var(--app-error)"
          backgroundColor="#FCF8EF"
        >
          {paymentLink && (
            <Box>
              <Text color={page.secondarytext} size="0.85rem" style={{ lineHeight: 1.25 }}>
                Your invoice is pending payment. If you have already completed the
                payment, please allow a few minutes for processing. Otherwise,
                please complete your payment at&nbsp;
                <a href={paymentLink} target="_blank" rel="noopener noreferrer" style={{ color: page.highlight }}>this link</a>
                &nbsp;to access all features.
              </Text>
            </Box>
          )}
        </ErrorNotice>
      )}
      {cancellationRequestedAt && !ghlScheduleCancelledAt && (
        <ErrorNotice
          message="Cancellation pending"
          messageColor="#AB5741"
          iconColor="#AB5741"
          backgroundColor="#FCF8EF"
        >
          <Box>
            <Text color={page.secondarytext} size="0.85rem" style={{ lineHeight: 1.25 }}>
              {'We\'re sorry to see you go. Your subscription will remain active until '}
              <strong>{cancellationNoticeEndDate}</strong>
              {'. You can withdraw your cancellation request before that date from '}
              <Link to={paths.cidCancellationRequest} style={{ color: page.highlight }}>
                your account page
              </Link>
              .
            </Text>
          </Box>
        </ErrorNotice>
      )}
      {ghlScheduleCancelledAt && (
        <ErrorNotice
          message="Limited access — billing cycle ended"
          messageColor="#AB5741"
          iconColor="#AB5741"
          backgroundColor="#FCF8EF"
        >
          <Box>
            <Text color={page.secondarytext} size="0.85rem" style={{ lineHeight: 1.25 }}>
              Your billing cycle has ended and most features are now disabled.
              {' '}
              <strong>Content ID access remains active</strong>
              {phase3EndDate ? (
                <>
                  {' until '}
                  <strong>{phase3EndDate}</strong>
                </>
              ) : null}
              {'. You can still withdraw your cancellation request from '}
              <Link to={paths.cidCancellationRequest} style={{ color: page.highlight }}>
                your account page
              </Link>
              .
            </Text>
          </Box>
        </ErrorNotice>
      )}
    </Box>
  );

  if (small) {
    return (
      <FullHeightBox background={page.background} pad={{ top: NAVBAR_DEFAULT_HEIGHT }}>
        {!hideBanner && bannerProps && (
          <AuthBanner
            small
            smallBanner={smallBanner}
            title={bannerProps.title}
            subTitle={bannerProps.subTitle}
            bannerChildren={bannerProps.bannerChildren || contentIdBannerChildren}
            bannerChildrenPosition={bannerProps.bannerChildrenPosition || 'right'}
            navCollapsed={navCollapsed}
            showPaymentNotice={renderErrorNotice}
          />
        )}
        <Box flex>
          {isDemo && (
            <Box
              background="rgba(255, 193, 7, 0.12)"
              border={{ side: 'bottom', color: 'rgba(255, 193, 7, 0.4)', size: '1px' }}
              pad={{ horizontal: '1.5rem', vertical: '0.65rem' }}
              direction="row"
              align="center"
              gap="0.5rem"
              justify="center"
            >
              <Text size="0.9rem" color="#b8860b" weight={500} textAlign="center">
                ⚠️ You&apos;re viewing a
                {' '}
                <strong>demo dashboard</strong>
                {' '}
                with sample data. Everything looks real, but nothing is connected —
                {' '}
                any changes you make won&apos;t be saved, and no actions will go through.
              </Text>
            </Box>
          )}
          {children}
        </Box>
        {cyclopsPageActive && !isCyclopsDemo && (
          <StyledStickyFooter align="center" justify="center" pad="1.5rem" background="#2F3C4A">
            <MenuDropdownSelect
              small={small}
              handleSelect={setActiveOrganization}
              options={companyOrganizations?.map(({ Id, name }) => ({ uid: Id, name }))}
              title="Organization"
              titleAlign="center"
              titleColor={page.secondarytext}
              selectLabel={organizationsActive ? activeOrganization.name : 'Select organization'}
              buttonLabel="Create an organization"
              buttonLabelColor={button.hoverText}
              buttonColor={button.hoverBg}
              buttonPath={paths.cyclopsOrganizations}
              secondaryButtonLabel="View organizations"
              selectMenuBg="white"
              selectButtonBg="white"
              selectButtonPrimary={page.primaryText}
              selectButtonHighlight={button.hoverText}
              selectHoverBg={button.hoverBg}
            />
          </StyledStickyFooter>
        )}
        {(!cyclopsPageActive || isCyclopsDemo) && (
          <Footer small companyMaintenanceSettings={companyMaintenanceSettings} />
        )}
      </FullHeightBox>
    );
  }

  return (
    <FullHeightBox
      background={page.background}
      pad={{ top: smallBanner ? 0 : SIDENAV_PAGE_CONTENTS_TOP_PAD }}
    >
      <Box direction="row">
        {!hideSideNav && (
          <Sidebar
            collapsed={navCollapsed}
            backgroundColor={navigation.background}
            width={SIDENAV_WIDTH}
            collapsedWidth={SIDENAV_COLLAPSED_WIDTH}
            transitionDuration={300}
            rootStyles={{
              position: 'fixed',
              top: '0',
              height: '100vh',
              borderColor: navigation.border,
              zIndex: 4,
            }}
          >
            <StyledMenu>
              <Box height="100%" direction="column" justify="between" pad={{ top: SIDENAV_TOP_PAD }}>
                <Box pad={{ horizontal: '0.75rem', top: '1.25rem' }}>
                  {withAnalytics && (
                    <>
                      <MenuSectionHeader label="Analytics" textColor={page.primaryText} collapsed={navCollapsed} />
                      <Box direction="column" gap="0.25rem">
                        {overviewEnabled && (
                          <MenuItemRenderer
                            first
                            location={location}
                            key="piracy-overview"
                            renderKey="piracy"
                            label="Piracy overview"
                            path={paths.productPiracyOverview}
                            collapsed={navCollapsed}
                            disabled={false}
                          />
                        )}
                        {(reportsEnabled || withCustomReports) && (
                          <MenuItemRenderer
                            first={!overviewEnabled}
                            location={location}
                            key="all-reports"
                            renderKey="reports"
                            label="All reports"
                            path={paths.productReports}
                            collapsed={navCollapsed}
                            disabled={false}
                          />
                        )}
                        {liveTrackerEnabled && (
                          <MenuItemRenderer
                            first={!overviewEnabled && !reportsEnabled && !withCustomReports}
                            location={location}
                            key="live-tracker"
                            renderKey="live-tracker"
                            label="Live tracker"
                            path={paths.productLiveTracker}
                            collapsed={navCollapsed}
                            disabled={false}
                          />
                        )}
                      </Box>
                    </>
                  )}
                  {!hideSearchFeatures && (
                    <>
                      {withAnalytics && <StyledHr color={navigation.border} />}
                      <MenuSectionHeader label="Results" textColor={page.primaryText} collapsed={navCollapsed} />
                      <Box direction="column" gap="0.25rem">
                        <MenuItemRenderer
                          first
                          location={location}
                          key="all-infringements"
                          renderKey="all-infringements"
                          label="All infringements"
                          path={paths.productInfringements}
                          collapsed={navCollapsed}
                          disabled={false}
                        />
                        <MenuItemRenderer
                          location={location}
                          key="confirmed-removals"
                          renderKey="confirmed-removals"
                          label="Confirmed removals"
                          path={paths.productConfirmedRemovals}
                          collapsed={navCollapsed}
                          disabled={false}
                        />
                        <MenuItemRenderer
                          location={location}
                          key="all-searches"
                          renderKey="all-searches"
                          label="All searches"
                          path={paths.productListSearches.replace(':status', 'all')}
                          collapsed={navCollapsed}
                          disabled={false}
                        />
                      </Box>
                    </>
                  )}
                  {contentIdEnabled && (
                    <>
                      {(withAnalytics || !hideSearchFeatures) && (
                        <StyledHr color={navigation.border} />
                      )}
                      <MenuSectionHeader label="Content ID" textColor={page.primaryText} collapsed={navCollapsed} />
                      <Box direction="column" gap="0.25rem">
                        {!isRedflagUpload && contentIdAssetUploadEnabled && (
                          <MenuItemRenderer
                            first
                            key="content-id-asset-upload"
                            location={location}
                            renderKey="content-id-asset-upload"
                            label="Upload assets"
                            path={paths.productAssetUpload}
                            collapsed={navCollapsed}
                            disabled={false}
                          />
                        )}
                        <MenuItemRenderer
                          first={isRedflagUpload}
                          location={location}
                          key="content-id-assets"
                          renderKey="content-id-assets"
                          label="Manage assets"
                          path={paths.productListAssets}
                          collapsed={navCollapsed}
                          disabled={false}
                        />
                        <MenuItemRenderer
                          location={location}
                          key="content-id-claims"
                          renderKey="content-id-claims"
                          label="Manage claims"
                          path={paths.productAllClaimsList}
                          pathMatch={(p) => p === paths.productAllClaimsList
                            || p.startsWith('/content-id/claims/')
                            || p.startsWith('/content-id/manual-claims')}
                          collapsed={navCollapsed}
                          disabled={false}
                        />
                        <MenuItemRenderer
                          location={location}
                          key="content-id-allowlist"
                          renderKey="content-id-allowlist"
                          label="Channel allowlist"
                          path={paths.productCidAllowlist}
                          collapsed={navCollapsed}
                          disabled={false}
                        />
                        {referralEnabled && (
                          <MenuItemRenderer
                            location={location}
                            key="content-id-referrals"
                            renderKey="content-id-referrals"
                            label="Creator referrals"
                            path={paths.creatorReferrals}
                            collapsed={navCollapsed}
                            disabled={false}
                          />
                        )}
                      </Box>
                    </>
                  )}
                  {(showSettings || showAltSettings) && (
                    <>
                      <StyledHr color={navigation.border} />
                      <MenuSectionHeader label="Settings" textColor={page.primaryText} collapsed={navCollapsed} />
                      <Box direction="column" gap="0.25rem">
                        {teamsSettingsEnabled && (
                          <MenuItemRenderer
                            first
                            location={location}
                            key="teams-settings"
                            renderKey="teams-settings"
                            label="Manage teams"
                            path={paths.teamsSettings}
                            collapsed={navCollapsed}
                            disabled={false}
                          />
                        )}
                        {unitsSettingsEnabled && (
                          <MenuItemRenderer
                            first={!teamsSettingsEnabled}
                            location={location}
                            key="units-settings"
                            renderKey="units-settings"
                            label={`Manage ${Case.lower(pluralize(unitTerm))}`}
                            path={paths.unitsSettings}
                            collapsed={navCollapsed}
                            disabled={false}
                          />
                        )}
                        {fingerprintingEnabled && hasFingerprintingConfig && (
                          <MenuItemRenderer
                            first={!teamsSettingsEnabled && !unitsSettingsEnabled}
                            key="fingerprinting-settings"
                            location={location}
                            renderKey="fingerprinting-settings"
                            label="Fingerprinting"
                            path={paths.fingerprintingSettings}
                            collapsed={navCollapsed}
                          />
                        )}
                        {showSettings && filteredCompanySettings.map(
                          ({ api, display: displayLabel }, i) => (
                            <MenuItemRenderer
                              key={api}
                              first={
                                i === 0 && !teamsSettingsEnabled && !unitsSettingsEnabled
                                && !(fingerprintingEnabled && hasFingerprintingConfig)
                              }
                              location={location}
                              renderKey={api}
                              label={settingsMenuConfig[api]?.label || displayLabel}
                              path={paths.settings.replace(':id', api)}
                              collapsed={navCollapsed}
                              disabled={false}
                            />
                          ),
                        )}
                      </Box>
                    </>
                  )}
                  {cyclopsEnabled && (
                    <>
                      {(withAnalytics || !hideSearchFeatures) && (
                        <StyledHr color={navigation.border} />
                      )}
                      <MenuSectionHeader label="Cyclops" textColor={page.primaryText} collapsed={navCollapsed} />
                      <Box direction="column" gap="0.25rem">
                        {!isCyclopsDemo && (
                          <animated.div style={{ opacity, display }}>
                            <Box pad={{ top: '0.35rem' }}>
                              <MenuDropdownSelect
                                small={small}
                                handleSelect={setActiveOrganization}
                                options={(
                                  companyOrganizations?.map(({ Id, name }) => ({ uid: Id, name }))
                                )}
                                title="Organization"
                                titleColor={page.secondarytext}
                                selectLabel={organizationsActive ? activeOrganization.name : 'Select organization'}
                                buttonLabel="Create an organization"
                                buttonLabelColor={page.primaryText}
                                buttonColor={isDarkMode ? '#454A59' : 'white'}
                                buttonPath={paths.cyclopsOrganizations}
                                secondaryButtonLabel="View organizations"
                                selectMenuBg={isDarkMode ? '#454A59' : 'white'}
                                selectButtonBg={isDarkMode ? '#454A59' : 'white'}
                                selectButtonPrimary={page.primaryText}
                                selectButtonHighlight={button.hoverText}
                                selectHoverBg={button.hoverBg}
                              />
                            </Box>
                          </animated.div>
                        )}
                        {!cyclopsAnalyticsDisabled && (
                          <MenuItemRenderer
                            first
                            location={location}
                            key="cyclops-analytics"
                            renderKey="analytics"
                            label="Analytics"
                            path={paths.cyclopsAnalytics}
                            collapsed={navCollapsed}
                            disabled={false}
                          />
                        )}
                        <MenuItemRenderer
                          first={cyclopsAnalyticsDisabled}
                          location={location}
                          key="cyclops-live-events"
                          renderKey="live-events"
                          label="Live events"
                          path={paths.cyclopsEventsList.replace(':status', 'live')}
                          collapsed={navCollapsed}
                          disabled={cyclopsMenuButtonsDisabled}
                        />
                        <MenuItemRenderer
                          location={location}
                          key="cyclops-past-events"
                          renderKey="past-events"
                          label="Past events"
                          path={paths.cyclopsEventsList.replace(':status', 'past')}
                          collapsed={navCollapsed}
                          disabled={cyclopsMenuButtonsDisabled}
                        />
                        {!cyclopsSettingsDisabled && (
                          <MenuItemRenderer
                            location={location}
                            key="cyclops-settings"
                            renderKey="settings"
                            label="Settings"
                            path={paths.cyclopsSettings}
                            collapsed={navCollapsed}
                            disabled={cyclopsMenuButtonsDisabled}
                          />
                        )}
                      </Box>
                    </>
                  )}
                </Box>
                <Box justify={navCollapsed ? 'center' : 'end'} align={navCollapsed ? 'center' : 'end'} pad={{ right: navCollapsed ? '0' : '0.5rem' }}>
                  <Button plain onClick={() => setNavCollapsed(!navCollapsed)}>
                    <animated.div style={{ transform, rotate: '180deg' }}>
                      <FormNext size="2.65rem" color={page.highlight} />
                    </animated.div>
                  </Button>
                </Box>
              </Box>
            </StyledMenu>
          </Sidebar>
        )}
        <Container direction="column" height={{ min: '100vh' }}>
          {!hideBanner && bannerProps && (
            <AuthBanner
              small={false}
              smallBanner={smallBanner}
              adjustLeftPad={hideSideNav}
              title={bannerProps.title}
              subTitle={bannerProps.subTitle}
              bannerChildren={bannerProps.bannerChildren || contentIdBannerChildren}
              bannerChildrenPosition={bannerProps.bannerChildrenPosition || 'right'}
              navCollapsed={navCollapsed}
              showPaymentNotice={renderErrorNotice}
            />
          )}
          <Box flex pad={!hideSideNav && { left: navCollapsed ? SIDENAV_COLLAPSED_WIDTH : SIDENAV_WIDTH }} style={{ transition: 'all 0.35s ease' }}>
            {isDemo && (
              <Box
                background="rgba(255, 193, 7, 0.12)"
                border={{ side: 'bottom', color: 'rgba(255, 193, 7, 0.4)', size: '1px' }}
                pad={{ horizontal: '1.5rem', vertical: '0.65rem' }}
                direction="row"
                align="center"
                gap="0.5rem"
                justify="center"
              >
                <Text size="0.9rem" color="#b8860b" weight={500} textAlign="center">
                  ⚠️ You&apos;re viewing a
                  {' '}
                  <strong>demo dashboard</strong>
                  {' '}
                  with sample data. Everything looks real, but nothing is connected —
                  {' '}
                  any changes you make won&apos;t be saved, and no actions will go through.
                </Text>
              </Box>
            )}
            {children}
          </Box>
          {!hideFooter && (
            <Footer small={small} companyMaintenanceSettings={companyMaintenanceSettings} />
          )}
        </Container>
      </Box>
    </FullHeightBox>
  );
};

function mapStateToProps(state) {
  const {
    companyId,
    companyName,
    companyFeatures,
    paymentPending,
    paymentLink,
    stripeAccountSetupComplete,
    stripeAccountId,
    isDemo,
  } = state.auth;
  const {
    activeCompanyOrganization: activeOrganization,
  } = state.organizations;
  const { companyOrganizations } = state.cyclops;
  const { companySettings, companyMaintenanceSettings } = state.companySettings;
  const {
    customerOnboardingData: onboardingData,
    customerOnboardingDataLoading: onboardingDataLoading,
    cancellationRequestedAt,
    effectiveCancellationDate,
    ghlScheduleCancelledAt,
  } = state.payment;
  const { customReportsConfig } = state.appConfig;

  return {
    companyId,
    companyName,
    companyFeatures,
    companyOrganizations,
    customReportsConfig,
    companySettings,
    companyMaintenanceSettings,
    activeOrganization,
    paymentPending,
    paymentLink,
    cancellationRequestedAt,
    effectiveCancellationDate,
    ghlScheduleCancelledAt,
    onboardingData,
    onboardingDataLoading,
    stripeAccountSetupComplete,
    stripeAccountId,
    isDemo,
  };
}

function mapDispatchToProps(dispatch) {
  return bindActionCreators({
    fetchCompanyOrganizations: initiateCyclopsCompanyOrganizationsRequest,
    setActiveOrganization: setCyclopsActiveOrganizationRequest,
    fetchCompanySettings: initiateCompanySettingsRequest,
    fetchCompanyMaintenanceSettings: initiateCompanyMaintenanceSettingsRequest,
    loadCancellationStatus: fetchCancellationStatus,
  }, dispatch);
}

AuthNavWrapper.propTypes = {
  small: PropTypes.bool.isRequired,
  location: PropTypes.shape({
    pathname: PropTypes.string.isRequired,
  }).isRequired,
  bannerProps: PropTypes.shape({
    title: PropTypes.string,
    subTitle: PropTypes.string,
    bannerChildren: PropTypes.node,
    bannerChildrenPosition: PropTypes.oneOf(['bottom', 'right']),
  }),
  companyFeatures: PropTypes.arrayOf(PropTypes.shape({
    key: PropTypes.string.isRequired,
    enabled: PropTypes.bool.isRequired,
    config: PropTypes.objectOf(PropTypes.any),
  })),
  customReportsConfig: PropTypes.arrayOf(PropTypes.any),
  companyId: PropTypes.number.isRequired,
  companyName: PropTypes.string.isRequired,
  paymentPending: PropTypes.bool,
  paymentLink: PropTypes.string,
  cancellationRequestedAt: PropTypes.string,
  effectiveCancellationDate: PropTypes.string,
  ghlScheduleCancelledAt: PropTypes.string,
  stripeAccountSetupComplete: PropTypes.bool,
  stripeAccountId: PropTypes.string,
  isDemo: PropTypes.bool,
  loadCancellationStatus: PropTypes.func.isRequired,
  children: PropTypes.node.isRequired,
  fetchCompanySettings: PropTypes.func.isRequired,
  companySettings: PropTypes.arrayOf(PropTypes.shape({
    api: PropTypes.string.isRequired,
    display: PropTypes.string.isRequired,
  })),
  onboardingData: PropTypes.shape({
    onboardingId: PropTypes.string.isRequired,
    companyId: PropTypes.number.isRequired,
    paymentPending: PropTypes.bool.isRequired,
    paymentLink: PropTypes.string,
    youtubeCid: PropTypes.shape({
      uploader: PropTypes.string,
      plan: PropTypes.string,
    }),
  }),
  onboardingDataLoading: PropTypes.bool.isRequired,
  fetchCompanyMaintenanceSettings: PropTypes.func.isRequired,
  companyMaintenanceSettings: PropTypes.shape({
    showStatusMessage: PropTypes.bool.isRequired,
    statusMessage: PropTypes.string.isRequired,
    disabledPages: PropTypes.arrayOf(PropTypes.shape({
      disableReviewFeatures: PropTypes.bool.isRequired,
      disableSearchFeatures: PropTypes.bool.isRequired,
      disableResultsFeatures: PropTypes.bool.isRequired,
      expiresAt: PropTypes.string.isRequired,
    })),
  }),
  setActiveOrganization: PropTypes.func.isRequired,
  fetchCompanyOrganizations: PropTypes.func.isRequired,
  companyOrganizations: PropTypes.arrayOf(PropTypes.any),
  activeOrganization: PropTypes.shape({
    uid: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
  }),
  smallBanner: PropTypes.bool,
  hideBanner: PropTypes.bool,
  hideFooter: PropTypes.bool,
  hideLeftNav: PropTypes.bool,
};

export default connect(mapStateToProps, mapDispatchToProps)(AuthNavWrapper);
